// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Fetch recent listings (created and approved within 3 days)
export const recentListings = () => {
  return request('/api/recent-listings', { method: methods.GET });
};

export const priceReductions = () => {
  return request('/api/price-reductions', { method: methods.GET });
};

export const featuredListings = () => {
  return request('/api/featured-listings', { method: methods.GET });
};

export const premiumListings = () => {
  return request('/api/premium-listings', { method: methods.GET });
};

export const similarListings = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const path = queryString ? `/api/similar-listings?${queryString}` : '/api/similar-listings';
  return request(path, { method: methods.GET });
};

export const authorListingsCount = (authorId) => {
  const queryString = new URLSearchParams({ authorId }).toString();
  return request(`/api/author-listings-count?${queryString}`, { method: methods.GET });
};

export const categoryListingsCount = (categoryLevel1) => {
  const queryString = new URLSearchParams({ categoryLevel1 }).toString();
  return request(`/api/category-listings-count?${queryString}`, { method: methods.GET });
};

// Batched category listings count - fetches multiple categories in one request
export const batchCategoryListingsCount = (categories) => {
  const params = new URLSearchParams();
  categories.forEach(cat => params.append('categories', cat));
  return request(`/api/batch-category-listings-count?${params.toString()}`, { method: methods.GET });
};

// Track listing visits (excluding owner visits)
// Records both logged-in and anonymous visits separately
export const trackListingVisit = (listingId) => {
  return post('/api/track-listing-visit', { listingId });
};

// Get analytics for a specific listing
export const getListingAnalytics = (listingId, options = {}) => {
  const params = new URLSearchParams(options);
  const queryString = params.toString();
  const path = queryString
    ? `/api/listing-analytics/${listingId}?${queryString}`
    : `/api/listing-analytics/${listingId}`;
  return request(path, { method: methods.GET });
};

// Get analytics for all user's listings
export const getUserListingsAnalytics = (options = {}) => {
  const params = new URLSearchParams(options);
  const queryString = params.toString();
  const path = queryString
    ? `/api/user-listings-analytics?${queryString}`
    : '/api/user-listings-analytics';
  return request(path, { method: methods.GET });
};

// Submit contact form
// Sends contact form data to backend which saves to MongoDB and sends email via SendGrid
export const submitContactForm = (formData) => {
  const baseUrl = apiBaseUrl();
  return window.fetch(`${baseUrl}/api/submit-contact-form`, {
    method: methods.POST,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  }).then(response => {
    if (!response.ok) {
      return response.json().then(data => {
        const error = new Error(data.message || 'Failed to submit contact form');
        error.data = data;
        throw error;
      });
    }
    return response.json();
  });
};
